import {
  Group,
  Box,
  Button,
  TagsInput,
  Select,
  PasswordInput,
} from "@mantine/core";
import React, { useState } from "react";
import { MultiGroupSelect } from "@/features/group/components/multi-group-select.tsx";
import { UserRole } from "@/lib/types.ts";
import { userRoleData } from "@/features/workspace/types/user-role-data.ts";
import { useCreateInvitationMutation } from "@/features/workspace/queries/workspace-query.ts";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Props {
  onClose: () => void;
}
export function WorkspaceInviteForm({ onClose }: Props) {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<string | null>(UserRole.MEMBER);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const createInvitationMutation = useCreateInvitationMutation();
  const navigate = useNavigate();

  async function handleSubmit() {
    const validEmails = emails.filter((email) => {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    });

    await createInvitationMutation.mutateAsync({
      role: role.toLowerCase(),
      emails: validEmails,
      groupIds: groupIds,
      // @ts-ignore: backend now accepts admin-set password
      password: password,
    });

    onClose();

    // Stay on the members tab — newly created users land in `users`
    // table immediately, not in the pending invites tab.
    navigate("");
  }

  const handleGroupSelect = (value: string[]) => {
    setGroupIds(value);
  };

  return (
    <>
      <Box maw="500" mx="auto">
        {/*<WorkspaceInviteSection /> */}

        <TagsInput
          mt="sm"
          description={t(
            "Enter valid email addresses separated by comma or space max_50",
          )}
          label={t("Invite by email")}
          placeholder={t("enter valid emails addresses")}
          variant="filled"
          splitChars={[",", " "]}
          maxDropdownHeight={200}
          maxTags={50}
          onChange={setEmails}
        />

        <PasswordInput
          mt="sm"
          label={t("Password *")}
          description={t(
            "Set the initial password for the new user(s). Min 8 characters. Share it with them out-of-band.",
          )}
          placeholder={t("Min 8 characters")}
          variant="filled"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />

        <Select
          mt="sm"
          description={t("Select role to assign to all invited members")}
          label={t("Select role")}
          placeholder={t("Choose a role")}
          variant="filled"
          data={userRoleData
            .filter((role) => role.value !== UserRole.OWNER)
            .map((role) => ({
              ...role,
              label: t(`${role.label}`),
              description: t(`${role.description}`),
            }))}
          defaultValue={UserRole.MEMBER}
          allowDeselect={false}
          checkIconPosition="right"
          onChange={setRole}
        />

        <MultiGroupSelect
          mt="sm"
          description={t(
            "Required. Invited users get space access exclusively via the groups they belong to.",
          )}
          label={t("Add to groups *")}
          onChange={handleGroupSelect}
        />

        <Group justify="flex-end" mt="md">
          <Button
            onClick={handleSubmit}
            loading={createInvitationMutation.isPending}
            disabled={
              emails.length === 0 ||
              groupIds.length === 0 ||
              password.length < 8
            }
          >
            {t("Create user(s)")}
          </Button>
        </Group>
      </Box>
    </>
  );
}
